import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { hydrateFromIndexedDB } from './hooks/useApiQuery';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';

// Safari bfcache: kad se stranica vrati iz back/forward cachea (event.persisted),
// prisilno osvježi da se učita najnoviji build (čest uzrok "zaglavljene stare verzije").
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (e) => { if (e.persisted) window.location.reload(); });
}

// Lazy load heavy pages - only loaded when navigated to
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LeadIntelligencePage = lazy(() => import('./pages/LeadIntelligencePage'));
const EmployerDetailPage = lazy(() => import('./pages/EmployerDetailPage'));
const CRMPage = lazy(() => import('./pages/CRMPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage'));
const StatisticsContractsPage = lazy(() => import('./pages/StatisticsContractsPage'));
const StatisticsTeamPage = lazy(() => import('./pages/StatisticsTeamPage'));
const ContractorsPage = lazy(() => import('./pages/ContractorsPage'));
const ITExpertsPage = lazy(() => import('./pages/ITExpertsPage'));
const CallStatsPage = lazy(() => import('./pages/CallStatsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const BoardPage = lazy(() => import('./pages/BoardPage'));
const AIAutomationPage = lazy(() => import('./pages/AIAutomationPage'));
// Cold Email = unified alat (kandidati + review queue + suppression + postavke). Bivša AutoEnrichPage komponenta.
const ColdEmailPage = lazy(() => import('./pages/AutoEnrichPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PonudePage = lazy(() => import('./pages/PonudePage'));
const MarketReportsPage = lazy(() => import('./pages/MarketReportsPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <div className="w-8 h-8 rounded-full border-3 border-amber-400 border-t-transparent animate-spin" />
  </div>
);

// Keyed wrapper: forsira potpuni remount EmployerDetailPage-a kad se promijeni :employerName.
// Time je SVAKO stanje (profil, bilješke, statusi…) garantirano svježe — nemoguće naslijediti
// podatke prethodno otvorene firme (npr. RADETIĆ → BURE bleed). Trajni fix za cross-contamination.
const KeyedEmployerDetail = () => {
  const { employerName } = useParams();
  return (
    <Suspense fallback={<PageLoader />}>
      <EmployerDetailPage key={employerName} />
    </Suspense>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
    },
  },
});

// Hydrate React Query cache from IndexedDB on startup
hydrateFromIndexedDB(queryClient).catch(() => {});

function ProtectedRoute({ children, roles, requireStats }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Učitavanje...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (requireStats && user.role !== 'admin' && !user.can_view_stats) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Učitavanje...</div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
        <Route path="leads" element={<Suspense fallback={<PageLoader />}><LeadIntelligencePage /></Suspense>} />
        <Route path="leads/:employerName" element={<KeyedEmployerDetail />} />
        {/* Alias — Dashboard hot leads link navigira na /employer/:name (pre-existing), pa ovdje routamo na isti EmployerDetailPage */}
        <Route path="employer/:employerName" element={<KeyedEmployerDetail />} />
        <Route path="crm" element={<Suspense fallback={<PageLoader />}><CRMPage /></Suspense>} />
        <Route path="faze-lidova" element={<Suspense fallback={<PageLoader />}><PipelinePage /></Suspense>} />
        <Route path="ploca" element={<Suspense fallback={<PageLoader />}><BoardPage /></Suspense>} />
        <Route path="history" element={<Suspense fallback={<PageLoader />}><HistoryPage /></Suspense>} />
        {/* Statistika dostupna SVIM korisnicima — backend non-admina self-scopea (vidi samo sebe) */}
        <Route path="statistics" element={<Suspense fallback={<PageLoader />}><StatisticsPage /></Suspense>} />
        <Route path="statistics/ugovori" element={<Suspense fallback={<PageLoader />}><StatisticsContractsPage /></Suspense>} />
        <Route path="statistics/tim" element={<Suspense fallback={<PageLoader />}><StatisticsTeamPage /></Suspense>} />
        <Route path="natjecaji" element={<Suspense fallback={<PageLoader />}><ContractorsPage /></Suspense>} />
        <Route path="it-strucnjaci" element={<Suspense fallback={<PageLoader />}><ITExpertsPage /></Suspense>} />
        <Route path="statistics/pozivi" element={<ProtectedRoute roles={['admin']}><Suspense fallback={<PageLoader />}><CallStatsPage /></Suspense></ProtectedRoute>} />
        <Route path="ai-automation" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AIAutomationPage /></Suspense></ProtectedRoute>} />
        <Route path="cold-email" element={<Suspense fallback={<PageLoader />}><ColdEmailPage /></Suspense>} />
        {/* Legacy redirect: stari /auto-enrich link → /cold-email (admini imaju dodatne tabove tamo) */}
        <Route path="auto-enrich" element={<Navigate to="/cold-email" replace />} />
        <Route path="admin" element={<ProtectedRoute roles={['admin']}><Suspense fallback={<PageLoader />}><AdminPage /></Suspense></ProtectedRoute>} />
        <Route path="profile" element={<Suspense fallback={<PageLoader />}><ProfilePage /></Suspense>} />
        <Route path="ponude" element={<Suspense fallback={<PageLoader />}><PonudePage /></Suspense>} />
        <Route path="trziste" element={<Suspense fallback={<PageLoader />}><MarketReportsPage /></Suspense>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => { console.log('[Lead Intel] Build 2026-04-01T10:45'); }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
